import { useCallback, useRef, useState } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useAuth from '../../../hooks/useAuth';
import useFetchSubCategories from '../../../hooks/useFetchSubCategories';
import { useAppSelector } from '../../../redux/helper';
import { useDispatch } from 'react-redux';
import { RootState } from '../../../redux/types';
import { getProducts } from '../../../api/products';
import { PRODUCT_LIMIT } from '../../../CONSTANTS';
import { addupdateProducts } from '../../../redux/productSlice';
import { ADD_PRODUCT_UNAUTHORIZED_MESSAGE } from '../../../CONSTANTS';
import { isEmployeeAdminRole } from '../../../utils/roles';
import { getApiMessage } from '../../../utils/apiError';
import type { LocationObject } from '../../../redux/locationSlice';
import { buildCreateProductPayload } from './buildCreateProductPayload';
import {
  getActiveVariantRows,
  getVariantValidationError,
  validateProductForm,
} from './productFormValidation';
import type {
  CreateProductResult,
  CreateProductSource,
  CreateProductSuccessContext,
  EditableVariant,
  ProductFormData,
  ProductFormErrors,
  ProductPriceType,
} from './productFormTypes';
import appAnalytics from '../../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../../services/appAnalyticsContext';

/**
 * Canonical product-creation orchestration (contract:
 * specs/001-new-owner-setup-wizard/contracts/product-creation.md).
 *
 * The pure pieces (`verifyProductLocation`, `runCreateProductSubmission`) are
 * dependency-injected so they are testable without rendering the screen;
 * `useCreateProductForm` wires them to the app's axios/auth/location state.
 */

export type ProductLocationFailure =
  | 'location_loading'
  | 'location_unavailable'
  | 'test_location_active'
  | 'location_header_mismatch';

export type VerifiedProductLocation = {
  id: string;
  name: string;
  type: 'real';
};

export type ProductLocationVerification =
  | { ok: true; location: VerifiedProductLocation }
  | { ok: false; failure: ProductLocationFailure };

export const verifyProductLocation = (params: {
  currentLocation: LocationObject | null | undefined;
  isLoading: boolean;
  /** When provided, must agree with the current location id. */
  interceptorLocationId?: string | null;
}): ProductLocationVerification => {
  const { currentLocation, isLoading, interceptorLocationId } = params;
  if (isLoading) {
    return { ok: false, failure: 'location_loading' };
  }
  if (!currentLocation || !currentLocation._id) {
    return { ok: false, failure: 'location_unavailable' };
  }
  if (currentLocation.bp_location_is_system_generated === true) {
    return { ok: false, failure: 'test_location_active' };
  }
  if (
    interceptorLocationId != null &&
    String(interceptorLocationId) !== String(currentLocation._id)
  ) {
    return { ok: false, failure: 'location_header_mismatch' };
  }
  return {
    ok: true,
    location: {
      id: String(currentLocation._id),
      name: String(currentLocation.bp_location_name || ''),
      type: 'real',
    },
  };
};

export const describeLocationFailure = (
  failure: ProductLocationFailure,
): string => {
  switch (failure) {
    case 'location_loading':
      return 'Your business location is still loading. Please try again in a moment.';
    case 'location_unavailable':
      return 'No business location is available yet. Please try again shortly.';
    case 'test_location_active':
      return 'You are on a test location. Switch to your real business location to add products.';
    case 'location_header_mismatch':
      return 'Your location context changed. Please try again.';
  }
};

export interface CreateProductSubmissionDeps {
  /** POST bp_mobile_add_product_information; must throw on network failure. */
  postCreateProduct(
    payload: ReturnType<typeof buildCreateProductPayload>,
    locationId: string | null,
  ): Promise<any>;
  /** Canonical products refresh; resolves true on success, false on failure. */
  refreshProducts(): Promise<boolean>;
  /** Canonical subcategories refresh; resolves true/false like above. */
  refreshSubCategories(): Promise<boolean>;
}

export const classifyCreateProductError = (
  error: any,
): Extract<CreateProductResult, { kind: 'failed' }> => {
  const message = String(error?.message || '');
  if (
    error?.code === 'ERR_NETWORK' ||
    /network error|server unreachable|check your connection/i.test(message)
  ) {
    return {
      kind: 'failed',
      category: 'offline',
      userMessage:
        'Could not reach the server. Check your connection and try again.',
    };
  }
  if (/authentication failed/i.test(message)) {
    return {
      kind: 'failed',
      category: 'authentication',
      userMessage: 'Your session expired. Please log in again.',
    };
  }
  return {
    kind: 'failed',
    category: 'unknown',
    userMessage: getApiMessage(error, 'Something went wrong'),
  };
};

/**
 * Runs the create request and classifies the response exactly as
 * NewProduct.tsx did pre-refactor: `data.status === 200` + `unsavedProducts`
 * duplicate detection + `data.data.savedProductIds` on success, followed by
 * the canonical products/subcategories refresh with observable outcome.
 */
export const runCreateProductSubmission = async (
  deps: CreateProductSubmissionDeps,
  input: {
    payload: ReturnType<typeof buildCreateProductPayload>;
    locationId: string | null;
    lock: { current: boolean };
  },
): Promise<CreateProductResult> => {
  if (input.lock.current) {
    return {
      kind: 'failed',
      category: 'validation',
      userMessage: 'A product is already being saved.',
    };
  }
  input.lock.current = true;
  try {
    const response = await deps.postCreateProduct(
      input.payload,
      input.locationId,
    );
    const message = String(response?.data?.message || '');
    if (response?.data?.status === 200) {
      const unsavedProducts = response?.data?.unsavedProducts;
      if (Array.isArray(unsavedProducts) && unsavedProducts.length > 0) {
        return {
          kind: 'duplicate_or_unsaved',
          unsavedProducts,
          message:
            'This product already exists, please add a different product',
        };
      }
      const savedProductIds = Array.isArray(
        response?.data?.data?.savedProductIds,
      )
        ? (response.data.data.savedProductIds as string[])
        : [];

      const [productsOk, subCategoriesOk] = await Promise.all([
        deps.refreshProducts().catch(() => false),
        deps.refreshSubCategories().catch(() => false),
      ]);
      return {
        kind: 'created',
        savedProductIds,
        refreshStatus: productsOk && subCategoriesOk ? 'succeeded' : 'failed',
        message,
      };
    }
    return {
      kind: 'failed',
      category: 'server',
      userMessage: message || 'Something went wrong',
    };
  } catch (error: any) {
    return classifyCreateProductError(error);
  } finally {
    input.lock.current = false;
  }
};

export interface UseCreateProductFormParams {
  source: CreateProductSource;
  /**
   * 'require_real' blocks submission on test/loading/missing locations
   * (wizard + tracker). 'allow_any' preserves the pre-refactor NewProduct
   * behavior of not gating on location.
   */
  locationPolicy?: 'require_real' | 'allow_any';
  onCreated?: (context: CreateProductSuccessContext) => void | Promise<void>;
}

export interface SubmitProductInput {
  formData: ProductFormData;
  priceType: ProductPriceType | string;
  hasVariants: boolean;
  variantAttributeNames: string[];
  variantRows: EditableVariant[];
  /** The Service segment of the product form. */
  isService?: boolean;
}

export type SubmitProductOutcome =
  | CreateProductResult
  | {
      kind: 'invalid';
      fieldErrors: ProductFormErrors;
      formError?: string;
    };

export const useCreateProductForm = (params: UseCreateProductFormParams) => {
  const {
    source,
    locationPolicy = source === 'default' || source === 'deeplink'
      ? 'allow_any'
      : 'require_real',
    onCreated,
  } = params;
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const dispatch = useDispatch();
  const fetchSubCategories = useFetchSubCategories();
  const employee = useAppSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo,
  );
  const currentLocation = useAppSelector(
    (state: RootState) => state.location.currentLocation,
  );
  const locationLoading = useAppSelector(
    (state: RootState) => state.location.isLoading,
  );
  const [submitting, setSubmitting] = useState(false);
  const lockRef = useRef(false);

  const refreshProducts = useCallback(async (): Promise<boolean> => {
    try {
      const response = await getProducts(axiosPrivate, 1, PRODUCT_LIMIT, auth);
      if (response?.data?.status === 200) {
        dispatch(addupdateProducts(response?.data.data));
        return true;
      }
      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  }, [auth, axiosPrivate, dispatch]);

  const refreshSubCategories = useCallback(async (): Promise<boolean> => {
    try {
      await fetchSubCategories();
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }, [fetchSubCategories]);

  const submit = useCallback(
    async (input: SubmitProductInput): Promise<SubmitProductOutcome> => {
      const activeVariantRows = getActiveVariantRows(input.variantRows);
      const variantValidationError = getVariantValidationError({
        hasVariants: input.hasVariants,
        variantAttributeNames: input.variantAttributeNames,
        activeVariantRows,
      });
      const validation = validateProductForm({
        formData: input.formData,
        priceType: input.priceType,
        hasVariants: input.hasVariants,
        variantValidationError,
        isService: input.isService,
      });
      if (!validation.valid) {
        return {
          kind: 'invalid',
          fieldErrors: validation.fieldErrors,
          formError: validation.formError,
        };
      }

      if (!isEmployeeAdminRole(employee?.bp_employee_role)) {
        return {
          kind: 'failed',
          category: 'authentication',
          userMessage: ADD_PRODUCT_UNAUTHORIZED_MESSAGE,
        };
      }

      let locationId: string | null = currentLocation?._id
        ? String(currentLocation._id)
        : null;
      if (locationPolicy === 'require_real') {
        const verification = verifyProductLocation({
          currentLocation,
          isLoading: locationLoading,
        });
        if (!verification.ok) {
          return {
            kind: 'failed',
            category: 'location',
            userMessage: describeLocationFailure(verification.failure),
          };
        }
        locationId = verification.location.id;
      }

      const payload = buildCreateProductPayload({
        formData: input.formData,
        priceType: input.priceType,
        hasVariants: input.hasVariants,
        variantAttributeNames: input.variantAttributeNames,
        activeVariantRows,
        isService: input.isService,
      });

      setSubmitting(true);
      try {
        const result = await runCreateProductSubmission(
          {
            postCreateProduct: (body, explicitLocationId) =>
              axiosPrivate.post(
                'bp_mobile_add_product_information',
                JSON.stringify(body),
                {
                  headers: {
                    Authorization: 'Bearer ' + auth.accessToken,
                    ...(explicitLocationId
                      ? { 'X-Location-ID': explicitLocationId }
                      : {}),
                  },
                },
              ),
            refreshProducts,
            refreshSubCategories,
          },
          { payload, locationId, lock: lockRef },
        );

        if (result.kind === 'created') {
          // Wizard owns product analytics via 001 owner_setup_product_*.
          if (source !== 'owner_setup') {
            appAnalytics.track('catalog_product_created', {
              ...buildAppAnalyticsContext({
                role: employee?.bp_employee_role,
                locationType: currentLocation?.bp_location_is_system_generated
                  ? 'test'
                  : currentLocation
                    ? 'real'
                    : undefined,
              }),
              source: 'pos',
              has_variants: input.hasVariants,
              ...(input.hasVariants
                ? { variant_count: activeVariantRows.length }
                : {}),
            });
          }
          if (onCreated) {
            await onCreated({
              source,
              savedProductIds: result.savedProductIds,
              locationId: locationId || '',
              locationType: 'real',
              refreshStatus: result.refreshStatus,
            });
          }
        }
        return result;
      } finally {
        setSubmitting(false);
      }
    },
    [
      auth,
      axiosPrivate,
      currentLocation,
      employee?.bp_employee_role,
      locationLoading,
      locationPolicy,
      onCreated,
      refreshProducts,
      refreshSubCategories,
      source,
    ],
  );

  return { submit, submitting };
};

export default useCreateProductForm;
